import { Component, inject, effect, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import {
  LangChangeEvent,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { filter, map, mergeMap } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { CONSTANTS_DATA } from 'paylink-constants';
import { ReactiveFormsModule } from '@angular/forms';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { Store } from '@ngrx/store';
import { ThemeInitService } from './services/theme/theme-init.service';
import { TrackingService } from './services/tracking.service';
import { MessageService } from 'primeng/api';

@Component({
  standalone: true,
  selector: 'fitness-force-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    ReactiveFormsModule,
    StoreDevtoolsModule,
    CommonModule,
    ToastModule,
    RouterOutlet,
    TranslateModule,
  ],
  providers: [MessageService],
})
export class AppComponent {
  title = 'UFP';
  textDir = signal<string>('ltr');

  private readonly translate = inject(TranslateService);
  private readonly titleService = inject(Title);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly themeInitService = inject(ThemeInitService);
  private readonly trackingService = inject(TrackingService);

  constructor() {
    // Initialize translation
    this.translate.setDefaultLang('en');
    this.translate.use('en');

    // Handle language changes
    effect(() => {
      const subscription = this.translate.onLangChange.subscribe(
        (event: LangChangeEvent) => {
          const lang = CONSTANTS_DATA.ALL_LANGUAGES.find(
            (lang) => lang.LANG_CODE === event.lang,
          );
          if (lang) {
            this.textDir.set(lang.dir);
          }
        },
      );

      return () => {
        subscription.unsubscribe();
      };
    });

    // Handle route changes for page title
    effect(() => {
      const subscription = this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          map(() => this.router.routerState.root),
          map((route) => {
            while (route.firstChild) route = route.firstChild;
            return route;
          }),
          mergeMap((route) => route.data),
        )
        .subscribe((data: any) => {
          const pageTitle = data.title || this.title;
          this.titleService.setTitle(pageTitle);
        });

      return () => {
        subscription.unsubscribe();
      };
    });

    // Initialize tracking infrastructure only (internal GTM dataLayer)
    // initCustomerTracking (company/tenant pixels) is called from feature components
    this.trackingService.initInternalTracking();
    
    // Initialize Anytime Fitness GTM (Layer 2) - domain-conditional
    this.trackingService.initAnytimeFitnessGTM();
  }
}
